import * as diff from 'diff';
import dayjs from 'dayjs';
import { OperationType, UpdatedChannels } from './print-channels.service';
import { ChannelGroup } from 'icreate/common/models/channel-group';
import { Vessel } from 'icreate/common/models/vessel';

export const PAGE_SIZE = 20;

export function pagesPerChannelGroup(channels) {
    let lastPages = [];
    for (let i = 0; i < channels.length; i++) {
        let channelsInGroup = channels[i].length;
        let lastPage = Math.ceil(channelsInGroup / PAGE_SIZE);
        if (i > 0) {
            lastPage = lastPage + lastPages[i - 1];
        }
        lastPages.push(lastPage);
    }
    return lastPages;
}

export function getHeader(currentPage, lastPages, tableGroupHeaders: string[]) {
    let header = '';
    for (let i = 0; i < lastPages.length; i++) {
        if (currentPage <= lastPages[i]) {
            header = tableGroupHeaders[i];
            break;
        }
    }

    return header;
}

export function getFooter(
    vessel: Vessel,
    pageNumber: number,
    groupNo: number,
    oldDrawingId?: string,
    DrawNo?: string,
    lastPages?: number[],
    groupPageNumber?
) {
    const isLastPageOfGroup = lastPages && lastPages[groupNo - 1] === pageNumber;
    const isLastPageOverall = lastPages && pageNumber === lastPages[lastPages.length - 1];

    const hasNextGroup = lastPages && groupNo < lastPages.length;

    const showNextDl = !isLastPageOverall && isLastPageOfGroup && hasNextGroup;

    const isDrawNoChanged = oldDrawingId && DrawNo && oldDrawingId !== DrawNo;

    const versionText = {
        text: `Version: ${vessel.projectVersion}`,
        border: [false, false, false, false],
        color: 'black',
    };

    const drawNoLabel = {
        text: `Draw No: `,
        color: 'black',
        margin: [20, 0, 0, 0],
        border: [false, false, false, false],
    };

    const drawNoValue = {
        text: DrawNo,
        color: isDrawNoChanged ? 'red' : 'black',
        border: isDrawNoChanged ? [true, true, true, true] : [false, false, false, false],
        borderColor: getRowBorderColor(),
    };

    const dlText = {
        text: `-DL${String(groupNo)}-${String(groupPageNumber)}`,
        border: [false, false, false, false],
        color: 'black',
    };

    const rightTable = {
        table: {
            body: [[versionText, drawNoLabel, drawNoValue, dlText]],
        },
        layout: {
            hLineColor: () => 'red',
            vLineColor: () => 'red',
        },
        alignment: 'right',
    };

    return [
        {
            columns: [
                {
                    text: `${dayjs().format('DD/MM/YY HH:mm')}${'\u00A0'.repeat(30)}Ship No: ${vessel.number}`,
                    alignment: 'left',
                },
                {
                    stack: [
                        rightTable,
                        ...(showNextDl
                            ? [
                                  {
                                      text: `NEXT-DL${String(groupNo + 1)}-1`,
                                      alignment: 'right',
                                  },
                              ]
                            : []),
                    ],
                    width: 'auto',
                },
            ],
            margin: [0, 0, 0, 0],
        },
    ];
}

export function getPdfLayout(updatedChannels: UpdatedChannels, deletedRowsInChannelGroup) {
    return {
        paddingTop: () => {
            return 0;
        },
        paddingBottom: () => {
            return 0;
        },
        paddingLeft: () => {
            return 0;
        },
        paddingRight: () => {
            return 0;
        },
        hLineWidth: (i, node) => {
            if (deletedRowsInChannelGroup?.includes(i - 1)) {
                if (node.table?.body[i][0]?.text) {
                    return 2;
                }
            }
            return 0.75;
        },
        vLineWidth: () => {
            return 0.75;
        },
        hLineColor: function (i, node) {
            if (node.table?.body[i]) {
                if (updatedChannels.newChannels?.includes(node.table?.body[i][0]?.text)) {
                    return 'red';
                }
                if (node.table?.body[i][0]?.text === '' && deletedRowsInChannelGroup?.includes(i - 1)) {
                    return 'red';
                }
                if (node.table?.body[i - 1]) {
                    if (updatedChannels.newChannels?.includes(node.table?.body[i - 1][0]?.text)) {
                        return 'red';
                    }
                    if (node.table?.body[i - 1][0]?.text === '' && deletedRowsInChannelGroup?.includes(i - 2)) {
                        return 'red';
                    }
                }
                if (node.table?.body[i][0]?.text !== '') {
                    if (deletedRowsInChannelGroup?.includes(i - 1)) {
                        return 'red';
                    }
                }
            }
            return 'black';
        },
        vLineColor: function () {
            return 'black';
        },
    };
}

export function getRowColor(channelDifferences, rowType, isRowDifferences) {
    if (!channelDifferences) {
        return 'black';
    }
    if (isRowDifferences || channelDifferences.modifiedFields?.some(item => rowType.includes(item))) {
        return 'red';
    }
    return 'black';
}

export function getRowBorder(channelDifferences, rowType, isRowDifferences) {
    if (!channelDifferences || isRowDifferences) {
        return [false, false, false, false];
    }
    if (channelDifferences.modifiedFields?.some(item => rowType.includes(item))) {
        return [true, true, true, true];
    }
    return [false, false, false, false];
}

export function getRowBorderColor() {
    return ['red', 'red', 'red', 'red'];
}

export function getInnerTableLayout(sidesSeparation?) {
    return {
        paddingLeft: function (i, node) {
            return sidesSeparation || 0;
        },
        paddingRight: function (i, node) {
            return sidesSeparation || 0;
        },
        paddingTop: function (i, node) {
            return 0;
        },
        paddingBottom: function (i, node) {
            return 0;
        },
    };
}

export function generateInnerTable(
    channelDifferences,
    rows: Array<{ text: any; fields: string[] }>,
    startColumn = false,
    endColumn = false,
    forceRedBorderTop = false,
    forceRedBorderMiddle = false
) {
    const isRowDifferences =
        channelDifferences?.operationType === OperationType.ADD ||
        channelDifferences?.operationType === OperationType.DELETE;

    const outerBorder = buildOuterBorder(forceRedBorderTop, forceRedBorderMiddle, rows.length);

    const body = rows.map((row, index) => {
        const forceRed = (index === 0 && forceRedBorderTop) || (index === 1 && forceRedBorderMiddle);

        return [
            {
                text: row.text,
                noWrap: true,
                color: getRowColor(channelDifferences, row.fields, isRowDifferences),
                border: forceRed
                    ? [true, true, true, true]
                    : getRowBorder(channelDifferences, row.fields, isRowDifferences),
                borderColor: getRowBorderColor(),
            },
        ];
    });

    if (!isRowDifferences && (forceRedBorderTop || forceRedBorderMiddle)) {
        body.forEach((row, i) => {
            if (!outerBorder[i].some(Boolean)) return;

            const border = startColumn
                ? [true, true, false, true]
                : endColumn
                  ? [false, true, true, true]
                  : [false, true, false, true];

            row[0] = {
                text: row[0].text,
                noWrap: true,
                color: 'red',
                border: border,
                borderColor: getRowBorderColor(),
            };
        });
    }

    return {
        table: {
            widths: ['*'],
            body: body,
            margin: [0, 0, 0, 0],
        },
        layout:
            !isRowDifferences && (forceRedBorderTop || forceRedBorderMiddle)
                ? {
                      ...getInnerTableLayout(),
                      vLineWidth: (i, node) => {
                          const isFirst = i === 0;
                          const isLast = i === node.table.widths.length;
                          const forceAnyRed = forceRedBorderTop || forceRedBorderMiddle;
                          if (!startColumn && isFirst) return 0;
                          if (!endColumn && isLast) return 0;
                          return isFirst || isLast ? 1 : 0;
                      },
                  }
                : getInnerTableLayout(),
        margin: !isRowDifferences && (forceRedBorderTop || forceRedBorderMiddle) ? [0, 0, 0, 0] : [1, 0, 1, 0],
        lineHeight: 0.8,
    };
}

function isToggle(oldValue: any, newValue: any) {
    return Boolean(oldValue) !== Boolean(newValue);
}

export function isEnableToggled(channelDifferences, enablePath: string) {
    const oldData = channelDifferences?.oldData;
    const newData = channelDifferences?.newData;

    const oldValue = enablePath.split('.').reduce((obj, key) => obj?.[key], oldData);
    const newValue = enablePath.split('.').reduce((obj, key) => obj?.[key], newData);

    return isToggle(oldValue, newValue);
}

function buildOuterBorder(isTopToggled: boolean, isMiddleToggled: boolean, rowsCount: number) {
    const border = new Array(rowsCount).fill(null).map(() => [false, false, false, false]);

    if (isTopToggled) border[0] = [true, true, true, true];
    if (isMiddleToggled) border[1] = [true, true, true, true];

    return border;
}

export function prepareChannelDifferences(channelsDifference) {
    let result = {};
    channelsDifference.differenceItems.map(difference => {
        if (
            difference.operationType === OperationType.UPDATE &&
            difference.modifiedFields.length === 1 &&
            difference.modifiedFields[0] === 'rowIndex'
        ) {
            return;
        }

        const oldName = difference.blob?.opsData?.name ?? '';
        const newName = difference.local?.opsData?.name ?? '';

        let operationType = difference.operationType;

        if (!oldName && newName) {
            operationType = OperationType.ADD;
        }
        if (oldName && !newName) {
            operationType = OperationType.DELETE;
        }

        result[difference.blob.id] = {
            operationType,
            modifiedFields: difference.modifiedFields,
            oldName,
            newName,
            oldChannelType: difference.blob?.opsData?.channelType,
            newChannelType: difference.local?.opsData?.channelType,
            oldData: difference.blob,
            newData: difference.local,
        };
        if (difference.operationType === OperationType.UPDATE && difference.modifiedFields.includes('opsData.name')) {
            result[difference.blob.id].oldName = difference.blob.opsData.name;
            result[difference.blob.id].newName = difference.local.opsData.name;
        }
    });
    return result;
}

export function getNewChannels(channelsDifference) {
    let newChannelsTags = [];
    channelsDifference.differenceItems.map(difference => {
        if (difference.operationType === OperationType.ADD) {
            newChannelsTags.push(difference.local.opsData.tag);
        }
    });
    return newChannelsTags;
}

export function getDeletedChannels(channelsDifference) {
    let deletedChannelRowsByGroup = {};
    channelsDifference.differenceItems.map(difference => {
        if (difference.operationType === OperationType.DELETE) {
            if (deletedChannelRowsByGroup[difference.local.groupId]) {
                deletedChannelRowsByGroup[difference.local.groupId].push(difference.oldRowPosition);
            } else {
                deletedChannelRowsByGroup[difference.local.groupId] = [difference.oldRowPosition];
            }
        }
    });
    return deletedChannelRowsByGroup;
}

export function prepareGroupDiffrences(groupDifference) {
    let result = {};
    groupDifference.differenceItems.map(difference => {
        result[difference.blob.id] = {
            operationType: difference.operationType,
            modifiedFields: difference.modifiedFields,
        };
        if (difference.operationType === OperationType.UPDATE && difference.modifiedFields.includes('displayName')) {
            result[difference.blob.id].oldName = difference.blob.displayName;
            result[difference.blob.id].newName = difference.local.displayName;
        }
        if (
            difference.operationType === OperationType.UPDATE &&
            difference.modifiedFields.includes('channelCategory')
        ) {
            result[difference.blob.id].oldCategory = difference.blob.channelCategory;
            result[difference.blob.id].newCategory = difference.local.channelCategory;
        }
    });
    return result;
}

export function getGroupHeaders(selectedGroups, groupsDifferences) {
    let tableHeaders = [];
    selectedGroups.forEach((group: ChannelGroup, i) => {
        const categoryLabel = group.channelCategory?.category || ' ';
        const groupNo = group.rowNum;

        const groupDifference = groupsDifferences[group.id];
        const isCategoryChanged = groupDifference?.modifiedFields?.some(field => field.includes('channelCategory'));

        const isGroupNameChanged =
            groupDifference?.oldName && groupDifference?.newName && groupDifference.oldName !== groupDifference.newName;

        const isGroupNoChanged = groupDifference?.modifiedFields?.includes('rowNum');

        const categoryText = {
            text: categoryLabel,
            color: isCategoryChanged ? 'red' : 'black',
            border: isCategoryChanged ? [true, true, true, true] : [false, false, false, false],
            borderColor: getRowBorderColor(),
            margin: [0, 0, 0, 1],
        };

        const groupNoText = {
            text: groupNo,
            color: isGroupNoChanged ? 'red' : 'black',
            border: isGroupNoChanged ? [true, true, true, true] : [false, false, false, false],
            borderColor: getRowBorderColor(),
            margin: [0, 0, 0, 1],
        };

        const groupNameText = {
            text: group.displayName,
            color: isGroupNameChanged ? 'red' : 'black',
            border: isGroupNameChanged ? [true, true, true, true] : [false, false, false, false],
            borderColor: getRowBorderColor(),
            margin: [0, 0, 0, 1],
        };

        const header = getNameDiff(group.displayName, groupDifference);
        header.margin = [14.1465, 55, 14.1465, 0];
        if (header.table) {
            header.table.body.unshift([
                {
                    text: categoryLabel,
                    border: [false, false, false, false],
                    margin: [0, 0, 0, 5],
                },
            ]);
            header.table.body.unshift([
                {
                    text: groupNo,
                    border: [false, false, false, false],
                    margin: [0, 0, 0, 5],
                },
            ]);
            header.table.body[1].unshift({
                text: 'Group Name: ',
                border: [false, false, false, false],
                margin: [0, 0, 0, 0],
            });
        } else {
            const CategoryText = {
                text: 'Category: ',
                margin: [0, 0, 0, 0],
                border: [false, false, false, false],
                color: 'black',
            };
            const GroupNoText = {
                text: '\tGroup No: ',
                margin: [0, 0, 0, 0],
                border: [false, false, false, false],
                color: 'black',
            };

            const GroupNameText = {
                text: '\tGroup Name: ',
                margin: [0, 0, 0, 0],
                border: [false, false, false, false],
                color: 'black',
            };

            header.table = {
                margin: [0, 0],
                body: [[CategoryText, categoryText, GroupNoText, groupNoText, GroupNameText, groupNameText]],
            };
            (header.layout as any) = {
                ...getInnerTableLayout(2),
                hLineColor: () => 'red',
                vLineColor: () => 'red',
            };
            header.lineHeight = 0.8;
            delete (header as any).text;
        }

        tableHeaders.push(header);
    });
    return tableHeaders;
}

export function getNameDiff(currentName, differences) {
    if (differences?.oldName) {
        const isChanged = differences?.oldName && differences?.newName && differences.oldName !== differences.newName;
        let tableBody = [];

        if (isChanged) {
            return { text: currentName, lineHeight: 0.8, margin: [0, 0, 0, 1] };
        }

        tableBody.push({
            text: currentName,
            color: isChanged ? 'red' : undefined,
            border: isChanged ? [true, true, true, true] : [false, false, false, false],
            borderColor: isChanged ? getRowBorderColor() : undefined,
            margin: [0, 0, 0, 1],
        });

        return {
            table: {
                margin: [0, 0],
                body: [tableBody],
            },
            layout: getInnerTableLayout(2),
            margin: [0, 0, 0, 1],
            lineHeight: 0.8,
        };
    }

    return { text: currentName, lineHeight: 0.8, margin: [0, 0, 0, 1] };
}
